import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  Keyboard,
  Platform,
  ScrollView,
  StyleSheet,
  TextInput,
  type ViewStyle,
} from 'react-native';
import { spacing } from '../constants/spacing';

type Props = {
  children: ReactNode;
  contentStyle?: ViewStyle;
};

export function KeyboardSafeForm({ children, contentStyle }: Props) {
  const scrollRef = useRef<ScrollView>(null);
  const scrollOffset = useRef(0);
  const [keyboardInset, setKeyboardInset] = useState(0);

  const revealFocusedInput = useCallback(() => {
    const delay = Platform.OS === 'ios' ? 80 : 160;
    setTimeout(() => {
      const input = TextInput.State.currentlyFocusedInput?.();
      const scroll = scrollRef.current;
      if (!input || !scroll) return;
      input.measureInWindow((_x, y, _w, h) => {
        const keyboardTop =
          Keyboard.metrics()?.screenY ?? y + h + spacing.xl;
        const covered = y + h + spacing.md - keyboardTop;
        if (covered > 0) {
          scroll.scrollTo({
            y: scrollOffset.current + covered,
            animated: true,
          });
        }
      });
    }, delay);
  }, []);

  useEffect(() => {
    const show = Keyboard.addListener('keyboardDidShow', event => {
      if (Platform.OS === 'android') {
        setKeyboardInset(event.endCoordinates.height);
      }
      revealFocusedInput();
    });
    const hide = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardInset(0);
    });
    return () => {
      show.remove();
      hide.remove();
    };
  }, [revealFocusedInput]);

  return (
    <ScrollView
      ref={scrollRef}
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        contentStyle,
        { paddingBottom: spacing.xl + keyboardInset },
      ]}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="interactive"
      automaticallyAdjustKeyboardInsets
      onScroll={event => {
        scrollOffset.current = event.nativeEvent.contentOffset.y;
      }}
      scrollEventThrottle={16}
    >
      {children}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, minHeight: 0 },
  content: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    gap: spacing.lg,
  },
});
